import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { getPhotoCredit } from "@/content/photoCredits";

/* Scroll-reveal wrapper — opacity+translate, opacity-only under reduced motion. */
export const Reveal = ({ children, delay = 0, className, as = "div" }) => {
  const reduce = useReducedMotion();
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      className={className}
      initial={reduce ? { opacity: 0 } : { opacity: 0, y: 14 }}
      whileInView={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay }}
    >
      {children}
    </MotionTag>
  );
};

/* Editorial button. variant: primary | secondary | ghost | light */
export const WfButton = React.forwardRef(
  (
    { children, variant = "primary", className, withArrow = false, as = "button", ...props },
    ref
  ) => {
    const base =
      "group inline-flex items-center justify-center gap-2 rounded-sm px-5 py-3 text-sm font-semibold tracking-[-0.005em] transition-[background-color,color,box-shadow,transform] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background active:scale-[0.98] min-h-11 motion-safe:hover:-translate-y-[1px]";
    const variants = {
      primary:
        "bg-[var(--wf-maroon)] text-[var(--wf-paper)] shadow-[var(--wf-shadow-sm)] hover:bg-[var(--wf-maroon-dark)] hover:shadow-[var(--wf-shadow)]",
      secondary:
        "border border-[var(--wf-farm-green)] text-[var(--wf-green-text)] bg-transparent hover:bg-[rgba(47,93,58,0.07)]",
      ghost:
        "bg-transparent text-foreground hover:bg-[rgba(31,35,32,0.06)]",
      light:
        "bg-[var(--wf-paper)] text-[var(--wf-maroon)] hover:bg-white shadow-[var(--wf-shadow-sm)]",
    };
    const Tag = as;
    return (
      <Tag ref={ref} className={cn(base, variants[variant], className)} {...props}>
        {children}
        {withArrow && (
          <ArrowRight
            className="h-4 w-4 motion-safe:transition-transform motion-safe:duration-200 motion-safe:group-hover:translate-x-0.5"
            aria-hidden="true"
          />
        )}
      </Tag>
    );
  }
);
WfButton.displayName = "WfButton";

/* Small pill kicker above headings */
export const Kicker = ({ children, className }) => (
  <span
    className={cn(
      "inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--wf-maroon)]",
      className
    )}
  >
    <span aria-hidden="true" className="h-px w-6 bg-[var(--wf-maroon)]/50" />
    {children}
  </span>
);

/* Tag / badge */
export const Tag = ({ children, tone = "wheat", className }) => {
  const tones = {
    wheat:
      "bg-[var(--wf-wheat)] text-[var(--wf-maroon)] border border-[var(--wf-border)]",
    green: "bg-[rgba(47,93,58,0.10)] text-[var(--wf-green-text)]",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold tracking-wide",
        tones[tone],
        className
      )}
    >
      {children}
    </span>
  );
};

/* Image with hover-revealed credit chip (always visible on touch). */
export const ImageWithCredit = ({
  src,
  alt,
  className,
  imgClassName,
  credit,
  href,
  loading = "lazy",
  interactive = true,
}) => {
  const resolved = getPhotoCredit(src);
  const creditLabel = credit === undefined ? resolved.label : credit;
  const creditHref = href === undefined ? resolved.href : href;
  const canLink = interactive && creditHref;
  return (
  <figure className={cn("group relative overflow-hidden", className)}>
    <img
      src={src}
      alt={alt}
      loading={loading}
      className={cn("h-full w-full object-cover", imgClassName)}
    />
    {creditLabel && (
      <figcaption className="absolute left-3 bottom-3 z-[2]">
        {canLink ? (
          <a
            href={creditHref}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-full bg-[rgba(255,253,248,0.9)] px-3 py-1 text-[11px] font-medium text-[var(--wf-soil)] backdrop-blur-sm opacity-100 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100 transition-opacity duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {creditLabel}
          </a>
        ) : (
          <span className="pointer-events-none block rounded-full bg-[rgba(255,253,248,0.9)] px-3 py-1 text-[11px] font-medium text-[var(--wf-soil)] backdrop-blur-sm opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-200">
            {creditLabel}
          </span>
        )}
      </figcaption>
    )}
  </figure>
  );
};

/* Photo credit chip — resolves the photographer from the image path. Renders a link when a URL is known. */
export const CreditChip = ({ src, className, ...rest }) => {
  const { label, href } = getPhotoCredit(src);
  if (!label) return null;
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className} {...rest}>
        {label}
      </a>
    );
  }
  return (
    <span className={className} {...rest}>
      {label}
    </span>
  );
};
